/**
 * deepLinkTab — map a full-page deep-link pathname to the platform activeTab it
 * should open on boot.
 *
 * The platform SPA is a single-page, tab-driven shell: on mount it restores the
 * last saved tab from localStorage and otherwise IGNORES window.location.pathname
 * (react-router boots the shell on the catch-all `/*`). A few URLs are emailed /
 * notified out-of-band and must instead land the user on a specific console:
 *   - /admin/observability → the observability alert page (opened by NotificationBell)
 *   - /agents              → the Agents console. The RCA pipeline's held /
 *                            blocked / stalled emails and in-app notifications all
 *                            link here (see _email_admins / the blocked watchdog).
 *                            The concrete tab is PRODUCT-AWARE: the WeaveIn build
 *                            opens the WeaveIn Agents console (weavein-agents, which
 *                            renders <AgentControlPage product="weavein" /> and is
 *                            gated to the ops org + super/org admin per migration
 *                            448), while the Triveny build opens the platform one
 *                            (platform-agents). The alert email is titled "Open the
 *                            WeaveIn Agents console" and FRONTEND_URL defaults to
 *                            weavein.ai, so on WeaveIn the link MUST resolve to
 *                            weavein-agents — mapping it to platform-agents dead-ends
 *                            for a WeaveIn ops admin, whose console tab is
 *                            weavein-agents, and lands them on a tab their role can't
 *                            render (TRUS-357). Query strings such as
 *                            /agents?job=123&alert=stalled arrive as location.search,
 *                            so the pathname is just "agents".
 *
 * Returns the target activeTab id, or null when the path is not an honored deep
 * link (the shell then restores the saved tab as before).
 */
export function deepLinkTabForPath(
  pathname: string,
  opts: { isTriveny?: boolean } = {},
): string | null {
  const path = (pathname || '').replace(/^\/+/, '').replace(/\/+$/, '');
  if (path === 'admin/observability') return 'admin/observability';
  // Every mailbox/calendar OAuth callback redirects the browser to
  // /settings/integrations?connected=<provider> or ?error=<code>. That path has no
  // route here, so the return landed on whatever tab was last saved and the user saw
  // no confirmation at all — a connect that had in fact worked looked like it had
  // done nothing (TRUS-517). Connected Services lives in User Preferences, so send
  // the return there; UserPreferences reads the query and opens the right tab.
  if (path === 'settings/integrations') return 'user-preferences';
  // A call invite (TRUS-668 v3) links to /workspace-messaging?call=ID: open the
  // Inbox, whose call host reads ?call= and opens the join flow.
  if (path === 'workspace-messaging') return 'workspace-messaging';
  // WeaveIn Verify (H.R. 1): the page_capabilities path is /verify.
  if (path === 'verify') return 'verify';
  if (path === 'agents' || path === 'platform-agents' || path === 'weavein-agents') {
    return opts.isTriveny ? 'platform-agents' : 'weavein-agents';
  }
  return null;
}
